import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FiCheck} from "react-icons/fi";
import {Link} from "react-router";
import * as Yup from "yup";

import DownIcon from "@/assets/images/down-icon.svg";
import Button from "@/components/button/Button";
import {CheckBoxField} from "@/components/form/fields/CheckBoxField.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {Input} from "@/components/input/Input.tsx";
import {URL_TERMS_AND_CONDITIONS} from "@/lib/links.ts";
import {GQL_PUBLIC_PROFILES_SUBSCRIBE} from "@/lib/queries/isitdown.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {PublicProfile, PublicProfilesSubscribeResponse} from "@/lib/types/isitdown.ts";

import styles from "./DowntimeNotifications.module.scss";

enum Subscription {
  SUBSCRIBED = "SUBSCRIBED",
  UNSUBSCRIBED = "UNSUBSCRIBED",
  ALREADY_SUBSCRIBED = "ALREADY_SUBSCRIBED",
}

interface PublicProfilesSubscribeFormValues {
  email?: string;
  target: string;
  terms_accepted: boolean;
}

interface SubscriptionInput extends PublicProfilesSubscribeFormValues {
  profile_id: string;
  unsubscribe?: boolean;
}

interface DowntimeNotificationsProps {
  profile: PublicProfile;
}

export const DowntimeNotifications = ({profile}: DowntimeNotificationsProps) => {
  const methods = useForm<PublicProfilesSubscribeFormValues>({
    resolver: yupResolver<PublicProfilesSubscribeFormValues>(
      Yup.object().shape({
        email: Yup.string(),
        target: Yup.string().email("Invalid email address").required("Email is required"),
        terms_accepted: Yup.bool().required().oneOf([true], "You must accept the Terms & Conditions"),
      })
    ),
    defaultValues: {
      email: "",
      target: "",
      terms_accepted: false,
    },
  });

  const [subscribed, setSubscribed] = useState<Subscription | null>(null);

  const [subscribeToAlerts, {loading}] = useMutation<PublicProfilesSubscribeResponse>(GQL_PUBLIC_PROFILES_SUBSCRIBE);

  const onSubmit = async (data: PublicProfilesSubscribeFormValues, unsubscribe?: boolean) => {
    const requestData: SubscriptionInput = {...data, profile_id: profile.id};
    if (unsubscribe) {
      requestData.unsubscribe = true;
    }
    const response = await subscribeToAlerts({
      variables: {data: requestData},
    });
    const result = response.data?.publicProfilesSubscribe;
    if (result?.status === ResponseStatus.SUCCESS) {
      setSubscribed(
        unsubscribe
          ? Subscription.UNSUBSCRIBED
          : result.data.subscribed
            ? Subscription.SUBSCRIBED
            : Subscription.ALREADY_SUBSCRIBED
      );
    } else if (result && "message" in result) {
      Object.entries(result.message).forEach(([field, messages]) => {
        methods.setError((field === "__all__" ? "target" : field) as keyof PublicProfilesSubscribeFormValues, {
          message: messages.join(", "),
        });
      });
    }
  };

  const onSubscribe = async (data: PublicProfilesSubscribeFormValues) => {
    return onSubmit(data);
  };

  const onUnsubscribe = async (data: PublicProfilesSubscribeFormValues) => {
    return onSubmit(data, true);
  };

  return (
    <div className={classNames(styles.getAlertedBanner, "marketing-bg-gradient")}>
      <div className={styles.getAlertedBannerBody}>
        <div className={styles.getAlertedBannerTop}>
          <div className={styles.getAlertedBannerIcon}>
            <img src={DownIcon} alt="Down Icon" title="Down Icon" />
          </div>
          <div className="marketingTextH2Bold pulse-color-gray-100 mb-4">
            Get alerted if <span className="pulse-color-green-500">{profile.name || profile.slug}</span> is down
          </div>
          <div className="marketingTextBody pulse-color-gray-400">
            Get free downtime notifications in your inbox when outages or incidents occur with this service.
          </div>
        </div>
        <div className={styles.getAlertedBannerDivider} />
        <div className={styles.getAlertedBannerBottom}>
          <FormProvider {...methods}>
            <form id="public-profiles-subscribe-form" onSubmit={methods.handleSubmit(onSubscribe, console.error)}>
              <input name="email" type="email" className="d-none" placeholder="This field is to be set by bots" />
              <InputField
                name="target"
                input={<Input type="email" {...methods.register("target")} placeholder="Enter your email" />}
                className="mb-3 pulseMaxWidthM"
              />
              <CheckBoxField
                name="terms_accepted"
                label={
                  <div className="marketingTextBodyTiny pulse-color-gray-300">
                    By clicking, you agree to the{" "}
                    <Link to={URL_TERMS_AND_CONDITIONS} className="pulse-color-gray-300">
                      Terms & Conditions
                    </Link>
                    .
                  </div>
                }
              />
              <Button variant="marketingPrimary" type="submit" disabled={loading} className={styles.subscribeButton}>
                Get Downtime Alerts
              </Button>
              {subscribed && (
                <div className="marketingTextBodyTiny pulse-color-gray-300 mt-3">
                  <Icon icon={FiCheck} className="pulse-color-green-500" display="inline" />
                  {subscribed === Subscription.ALREADY_SUBSCRIBED ? (
                    <>
                      <span>This email is already subscribed to alerts. To unsubscribe, </span>
                      <Button
                        variant="linkGreen"
                        size="fitContent"
                        typography="marketingLinkTiny"
                        className="d-inline-block"
                        onClick={methods.handleSubmit(onUnsubscribe, console.error)}
                      >
                        click here...
                      </Button>
                    </>
                  ) : subscribed === Subscription.SUBSCRIBED ? (
                    "You have been subscribed to the list."
                  ) : (
                    "Your email has been removed from the list."
                  )}
                </div>
              )}
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
